import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  message,
  Modal,
  Select,
  Spin,
  Space,
  Table,
  Typography
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { request } from "../services/api";
import { AppContext } from "../context/AppContext";

const { Title, Text } = Typography;
const { TextArea } = Input;

const LevelOne = () => {
  // eslint-disable-next-line
  const { levelOneOptions, setLevelOneOptions, totalOptions } =
    useContext(AppContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [data, setData] = useState(levelOneOptions || []);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [nextOption, setNextOption] = useState(0);
  const [isIntentChange, setIsIntentChange] = useState(false);
  const [isFulfilment, setIsFulfilment] = useState(false);

  const [modalMode, setModalMode] = useState("create");
  const [currentRecord, setCurrentRecord] = useState(null);

  useEffect(() => {
    start();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (nextOption) {
      form.setFieldsValue({
        optionLevelOne: nextOption
      });
    }
  }, [isModalOpen, nextOption, form]);

  const onActionChange = (value) => {
    if (value !== "") {
      console.log(value);
      switch (value) {
        case "transferToIntent":
          setIsFulfilment(false);
          setIsIntentChange(true);
          break;
        case "fulfilIntent":
          setIsIntentChange(false);
          setIsFulfilment(true);
          break;
        default:
          console.log("reset");
          setIsIntentChange(false);
          setIsFulfilment(false);
      }
    }
  };

  const openCreateModal = () => {
    setModalMode("create");
    setCurrentRecord(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setIsFulfilment(false);
    setModalMode("edit");
    setCurrentRecord(record);
    form.setFieldsValue(record);
    setIsModalOpen(true);
    if (record.action === "fulfilIntent") {
      setIsFulfilment(true);
    }
  };

  const renameKeyInArray = async (arr, oldKey, newKey) => {
    return arr.map((obj) => {
      const { [oldKey]: oldValue, ...rest } = obj;
      return {
        ...rest,
        [newKey]: oldValue
      };
    });
  };

  const start = async () => {
    try {
      const params = ``;
      setLoading(true);
      const response = await request(`/management/one/retrieve?${params}`, {
        method: "GET"
      });
      const menuOptionsLevelOneValues = await renameKeyInArray(
        response,
        "optionLevelOne",
        "value"
      );
      const menuOptionsLevelOneLabels = await renameKeyInArray(
        menuOptionsLevelOneValues,
        "name",
        "label"
      );
      const safeOptions = menuOptionsLevelOneLabels.map(
        ({ action, ...rest }) => ({
          ...rest,
          intentAction: action
        })
      );
      const indexedData = response.map((item, index) => ({
        ...item,
        key: item.id || index
      }));
      setLevelOneOptions(safeOptions);
      setData(indexedData);
      setNextOption(indexedData[indexedData.length - 1]?.optionLevelOne + 1);
    } catch (error) {
      messageApi.error("No se pudo iniciar la consulta");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    try {
      setLoading(true);
      let datosAEnviar = { ...values };
      await request("/management/one/create", {
        method: "POST",
        body: JSON.stringify(datosAEnviar)
      });
      messageApi.success(`Registro creado`);
      setIsModalOpen(false);
      form.resetFields();
    } catch (error) {
      messageApi.error("Error al crear contacto");
      console.log("option cannot be added");
    } finally {
      setLoading(false);
      console.log("option added successfully");
      start();
    }
  };

  const removeAndResequence = (arr, removedValue) => {
    return arr
      .filter((item) => item.optionLevelOne !== removedValue)
      .map((item) => {
        if (item.optionLevelOne > removedValue) {
          return { ...item, optionLevelOne: item.optionLevelOne - 1 };
        }
        return item;
      });
  };

  const getItemsToUpdateAfterRemoval = (
    arr,
    removedValue,
    field = "optionLevelOne"
  ) => {
    return arr
      .filter((item) => item?.[field] > removedValue)
      .map((item) => ({
        ...item,
        previousLevelOne: item[field], // valor antes del ajuste
        [field]: item[field] - 1 // nuevo valor a guardar
      }));
  };

  const handleDelete = async (recordAEliminar) => {
    const { optionLevelOne } = recordAEliminar;
    try {
      setLoading(true);
      await request(
        `/management/one/remove?optionLevelOne=${Number(optionLevelOne)}`,
        {
          method: "DELETE"
        }
      );
      // eslint-disable-next-line
      const newData = removeAndResequence(data, optionLevelOne);
      const dataToUpdate = getItemsToUpdateAfterRemoval(data, optionLevelOne);
      if (dataToUpdate.length > 0) {
        await request("/management/one/sort", {
          method: "PATCH",
          body: JSON.stringify(dataToUpdate)
        });
      }
    } catch (error) {
      messageApi.error("No se pudo eliminar");
    } finally {
      setLoading(false);
      console.log("option removed successfully");
      start();
    }
  };

  const handleUpdate = async (recordAActualizar) => {
    try {
      delete recordAActualizar.key;
      setLoading(true);
      await request("/management/one/update", {
        method: "PATCH",
        body: JSON.stringify(recordAActualizar)
      });
    } catch (error) {
      messageApi.error("No se pudo actualizr");
    } finally {
      setLoading(false);
      console.log("option updated successfully");
      start();
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (modalMode === "create") {
        await handleCreate(values);
      } else {
        const { optionLevelOne, ...newCurrentRecord } = values;
        const newValues = { ...currentRecord, ...newCurrentRecord };
        await handleUpdate(newValues);
      }
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {}
  };

  const columns = [
    {
      title: "Posición",
      dataIndex: "optionLevelOne",
      key: "optionLevelOne",
      width: 100
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name"
    },
    { title: "Acción", dataIndex: "action", key: "action", width: 200 },
    { title: "Intención", dataIndex: "intent", key: "intent", width: 200 },
    {
      title: "Mensaje",
      dataIndex: "message",
      key: "message"
    },
    {
      title: "Actividades",
      key: "remover",
      fixed: "right",
      width: 110,
      render: (_, record) => {
        const isDeleteDisabled = record.action === "none";

        return (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
            <Popconfirm
              title="Eliminar opción de menú"
              description={`¿Estás seguro de que deseas eliminar esta opción?, todas las dependencias serán eliminadas también`}
              onConfirm={() => handleDelete(record)}
              okText="Sí, eliminar"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={isDeleteDisabled}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isDeleteDisabled}
              />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      {contextHolder}
      <div style={{ marginBottom: 32 }}>
        <Title level={2} style={{ margin: 0 }}>
          Menú principal
        </Title>
        <Text type="secondary">
          Alta y baja de las opciones del menú principal.
        </Text>
        <br />
        <Space style={{ marginTop: 16, marginBottom: 16 }}>
          <Button icon={<PlusOutlined />} onClick={() => openCreateModal(true)}>
            Nueva Opción
          </Button>
        </Space>
        <Card>
          <Spin spinning={loading} description="Cargando información...">
            <Table
              dataSource={data}
              columns={columns}
              rowKey="key"
              pagination={false}
              tableLayout="fixed"
            />
          </Spin>
        </Card>
        <Modal
          title={modalMode === "create" ? "Nuevo registro" : "Editar registro"}
          open={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          confirmLoading={loading}
          okText="Guardar"
          cancelText="Cancelar"
          updateText="Actualizar"
          okButtonProps={{ style: { background: "#000" } }}
          forceRender={true}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="optionLevelOne"
              label="Posición"
              rules={[
                { required: true, message: "Ingresa la posición de la opción" }
              ]}
            >
              <Input
                placeholder="ejemplo: Atención a clientes"
                style={{ width: "70px" }}
                disabled
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Nombre"
              rules={[
                { required: true, message: "Ingresa el nombre de la opción" }
              ]}
            >
              <Input placeholder="ejemplo: Atención a clientes" />
            </Form.Item>
            <Form.Item
              name="action"
              label="Acción"
              rules={[
                { required: true, message: "Ingresa la acción asignada" }
              ]}
            >
              <Select
                style={{ width: "200px" }}
                placeholder="Selecciona primer nivel"
                onChange={onActionChange}
                options={[
                  { label: "Ninguna", value: "none" },
                  { label: "Transferir con Asesor", value: "transferToAgent" },
                  { label: "Cambiar Intención", value: "transferToIntent" },
                  { label: "Finalizar", value: "fulfilIntent" }
                ]}
              />
            </Form.Item>
            {isIntentChange ? (
              <Form.Item
                name="intent"
                label="Intención"
                rules={[
                  {
                    required: true,
                    message: "Ingresa la intención a la que te quieres mover"
                  }
                ]}
              >
                <Select
                  style={{ width: "200px" }}
                  placeholder="Selecciona primer nivel"
                  //onChange={onActionChange}
                  options={[
                    { label: "Consulta de Ticket", value: "ConsultaTicket" }
                  ]}
                />
              </Form.Item>
            ) : null}
            {isFulfilment ? (
              <Form.Item
                name="message"
                label="Mensaje de cierre"
                rules={[
                  {
                    required: true,
                    message:
                      "Ingresa el mensaje con el que quieres cerrar la interacción"
                  }
                ]}
              >
                <TextArea
                  rows={6}
                  placeholder="ejemplo: Muchas gracias por su tiempo, nosotros le buscaremos."
                />
              </Form.Item>
            ) : null}
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default LevelOne;
